import { useAppShell } from "@/app-bridge/hooks/useAppShell";
import { lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Mail, Menu, Shield, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useWebHandoff } from "@/app-bridge/hooks/useWebHandoff";
import { useIsAppMode } from "@/app-bridge/hooks/useIsAppMode";
import { isAppMode } from "@/app-bridge/core/wrapper";
import { useLocale, useTranslation } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";
const ThemeToggle = lazy(() =>
  import("@/components/ThemeToggle").then((module) => ({ default: module.ThemeToggle })),
);
import { getUnreadCount } from "@/lib/messages.functions";
import { getMyRole } from "@/lib/admin.functions";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { myProfileQuery } from "@/lib/queries";
import { adImageSrc } from "@/lib/image-src";
import { Button } from "@/components/ui/button";
import type { LegalDoc } from "@/components/legal/LegalModal";
import { SiteLogo } from "@/lib/brand";
import { LocalePicker } from "@/components/locale/LocalePicker";

const AuthModal = lazy(() =>
  import("@/components/auth/AuthModal").then((module) => ({ default: module.AuthModal })),
);
const LegalModal = lazy(() =>
  import("@/components/legal/LegalModal").then((module) => ({ default: module.LegalModal })),
);
const AdminInboxHover = lazy(() =>
  import("@/components/AdminInboxHover").then((module) => ({ default: module.AdminInboxHover })),
);
// Kontomenyn och mobilmenyn hämtas först när de behövs – de drar med sig
// Radix-menyer och ett trettiotal ikoner som utloggade besökare aldrig ser.
const UserMenu = lazy(() =>
  import("@/components/header/UserMenu").then((module) => ({ default: module.UserMenu })),
);
const MobileMenuSheet = lazy(() =>
  import("@/components/header/MobileMenuSheet").then((module) => ({
    default: module.MobileMenuSheet,
  })),
);

export function SiteHeader() {
  const { user, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const appLike = useAppShell();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMounted, setMobileMounted] = useState(false);
  const [doc, setDoc] = useState<LegalDoc>(null);
  const [webBannerDismissed, setWebBannerDismissed] = useState(false);
  const { openOnWeb } = useWebHandoff();
  // Läses via hook (efter hydrering) så server och klient renderar likadant.
  const inApp = useIsAppMode();
  const showWebBanner = inApp && !webBannerDismissed;
  const fetchUnread = useServerFn(getUnreadCount);
  const { data: unread } = useQuery({
    queryKey: ["unread", user?.id],
    queryFn: () => fetchUnread({}),
    enabled: !!user,
    refetchInterval: 15000,
  });
  const unreadCount = unread?.count ?? 0;
  const fetchRole = useServerFn(getMyRole);
  const { data: roleData } = useQuery({
    queryKey: ["my-role", user?.id],
    queryFn: () => fetchRole({}),
    enabled: !!user,
  });
  const isAdminUser = !!roleData?.isAdmin;
  const { data: adminInbox } = useQuery({
    queryKey: ["admin-inbox-summary"],
    // Adminsummeringen hämtas som eget paket – bara administratörer behöver den.
    queryFn: async () => (await import("@/lib/admin-inbox.functions")).getAdminInboxSummary({}),
    enabled: isAdminUser,
    refetchInterval: 60000,
  });
  const adminInboxTotal = adminInbox?.total ?? 0;
  const adminSupport = adminInbox?.support ?? 0;

  const fetchPlan = useServerFn(getMyPlanStatus);
  const { data: planStatus } = useQuery({
    queryKey: ["my-plan"],
    queryFn: () => fetchPlan({}),
    enabled: !!user,
  });
  const { data: profile, isPending: profilePending } = useQuery({
    ...myProfileQuery(),
    enabled: !!user,
  });
  const displayName = profile?.displayName?.trim() || null;
  const avatarSrc = profile?.avatarUrl ? adImageSrc(profile.avatarUrl) : null;

  const go = (to: string) => {
    setMobileOpen(false);
    navigate({ to: to as never });
  };

  const openMobile = () => {
    setMobileMounted(true);
    setMobileOpen((v) => !v);
  };

  const { t } = useTranslation();
  const { lang } = useLocale();
  const local = (to: string) => localizedPath(to, lang);

  const navLinkClass =
    "whitespace-nowrap text-sm font-medium text-foreground/80 transition-colors hover:text-foreground";

  const nav = (
    <>
      <Link to={local("/annonser")} className={navLinkClass}>
        {t("nav.allAds")}
      </Link>
      <Link to={local("/skapa")} className={navLinkClass}>
        {t("nav.createAd")}
      </Link>
      <Link to={local("/priser")} className={navLinkClass}>
        {t("nav.pricing")}
      </Link>
      <Link to={local("/tips")} className={navLinkClass}>
        {t("nav.tips")}
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border/80 bg-background/90 backdrop-blur">
      {showWebBanner && (
        <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1.5 border-b border-border/60 bg-muted/60 px-3 py-1.5 text-xs text-muted-foreground sm:flex sm:justify-center sm:gap-2 sm:text-sm">
          <span className="min-w-0">{t("common.webBanner")}</span>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1 px-2 text-xs"
            onClick={() => openOnWeb()}
          >
            <ExternalLink className="h-3 w-3" /> {t("common.openOnWeb")}
          </Button>
          <button
            type="button"
            aria-label="Stäng"
            onClick={() => setWebBannerDismissed(true)}
            className="grid h-9 w-9 place-items-center text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:px-8">
        <Link to={local("/")} aria-label={t("nav.sellavoHome")} className="lg:justify-self-start">
          <SiteLogo className="h-7 sm:h-8" />
        </Link>

        <nav className="hidden items-center gap-6 justify-self-center lg:flex">{nav}</nav>

        <div className="flex flex-1 items-center justify-end gap-1 sm:gap-2 lg:justify-self-end">
          {/* I appen och som installerad PWA bor utseendet i menyn i stället. */}
          {!appLike && (
            <Suspense fallback={<span className="h-9 w-9" />}>
              <ThemeToggle />
            </Suspense>
          )}
          <LocalePicker />
          {/* På mobil ligger konto, inkorg och inloggning i den fasta bottenmenyn.
              Från md finns ingen bottenmeny, så då visas de här uppe igen. */}
          <div className="hidden items-center gap-1 sm:gap-2 md:flex">
            {authLoading ? (
              <div className="h-8 w-24 animate-pulse rounded-md bg-muted" aria-hidden />
            ) : user ? (
              <>
                <Suspense fallback={null}>
                  <AdminInboxHover enabled={isAdminUser} unreadCount={unreadCount}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="relative gap-2 px-2 sm:px-3"
                      onClick={() => navigate({ to: local("/meddelanden") })}
                      aria-label={
                        unreadCount > 0
                          ? t("nav.inboxUnread", { count: unreadCount })
                          : t("common.inbox")
                      }
                      title={
                        unreadCount > 0
                          ? t("nav.inboxUnread", { count: unreadCount })
                          : t("common.inbox")
                      }
                    >
                      <Mail className="h-4 w-4 shrink-0" />
                      <span className="hidden lg:inline">{t("common.inbox")}</span>
                      {(unreadCount > 0 || (isAdminUser && adminInboxTotal > 0)) && (
                        <span className="pointer-events-none absolute -right-1 -top-2 flex items-start gap-0.5">
                          {isAdminUser && adminInboxTotal > 0 && (
                            <span
                              className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500/20 px-[2px] py-0 text-[9px] font-bold leading-none text-amber-600 shadow-sm ring-1 ring-background dark:text-amber-400"
                              title={`${adminInboxTotal} adminärenden väntar${adminSupport > 0 ? ` – varav ${adminSupport} support` : ""}`}
                            >
                              <Shield className="h-1 w-1" />
                              {adminInboxTotal}
                            </span>
                          )}
                          {unreadCount > 0 && (
                            <span className="min-w-5 rounded-full bg-primary px-1.5 py-0.5 text-center text-[11px] font-semibold leading-none text-primary-foreground shadow-sm ring-1 ring-background">
                              {unreadCount}
                            </span>
                          )}
                        </span>
                      )}
                    </Button>
                  </AdminInboxHover>
                </Suspense>

                <Suspense
                  fallback={
                    <div className="h-8 w-24 rounded-md border border-border" aria-hidden />
                  }
                >
                  <UserMenu
                    email={user.email ?? ""}
                    displayName={displayName}
                    avatarSrc={avatarSrc}
                    plan={planStatus?.plan}
                    isAdmin={isAdminUser}
                    profilePending={profilePending}
                    onNavigate={(to) => navigate({ to: to as never })}
                    onSignOut={async () => {
                      await signOut();
                      navigate({ to: "/" });
                    }}
                  />
                </Suspense>
              </>
            ) : (
              <Button size="sm" onClick={() => setAuthOpen(true)}>
                {t("common.signIn")}
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={t("common.more")}
            onMouseEnter={() => setMobileMounted(true)}
            onTouchStart={() => setMobileMounted(true)}
            onClick={openMobile}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>
      {mobileMounted && (
        <Suspense fallback={null}>
          <MobileMenuSheet
            open={mobileOpen}
            onOpenChange={setMobileOpen}
            email={user?.email ?? null}
            displayName={displayName}
            avatarSrc={avatarSrc}
            plan={planStatus?.plan}
            unreadCount={unreadCount}
            isAdmin={isAdminUser}
            onNavigate={go}
            onOpenAuth={() => {
              setMobileOpen(false);
              setAuthOpen(true);
            }}
            onOpenLegal={() => {
              setDoc("integritet");
              setMobileOpen(false);
            }}
            onSignOut={async () => {
              await signOut();
              setMobileOpen(false);
              navigate({ to: "/" });
            }}
          />
        </Suspense>
      )}
      {authOpen && (
        <Suspense fallback={null}>
          <AuthModal open onOpenChange={setAuthOpen} />
        </Suspense>
      )}
      {doc && (
        <Suspense fallback={null}>
          <LegalModal doc={doc} onClose={() => setDoc(null)} />
        </Suspense>
      )}
    </header>
  );
}
